/**
 * 알파벳 모듈 — "무엇이 글자이고 무엇이 닮았는가"를 로케일별로 답한다(RULES §1.17 · ADR-047).
 * 한글은 기존 `hangul.ts` 를 그대로 감싼다(바이트 단위 동일 · 골든이 지킨다). 라틴은 혼동 부류로 유사도를 정의한다.
 * 순수 함수뿐이다 — DOM·시계·난수 없음.
 */
import { decompose, buildLadder as hangulLadder, jamoSimilarity, normalizeJamo } from './hangul';

export type AlphabetId = 'hangul' | 'latin';
export type Locale = 'ko' | 'en';

export interface Alphabet {
  readonly id: AlphabetId;
  /** 규칙 11 — 사다리 최소 길이 */
  readonly ladderMin: number;
  /** 규칙 12 — 정답 자리마다 필요한 함정 후보 수 */
  readonly trapMinPerSlot: number;
  /** 정답 정규식 조각 ('가-힣' · 'A-Z') */
  readonly charClass: string;
  /** 사람 언어 이름 — 검증 메시지용 */
  readonly charLabel: string;
  isTile(ch: string): boolean;
  /** 0..3 — 2 이상이면 함정(닮았다) */
  similarity(a: string, b: string): number;
  /** 오답 사다리 — 팩 빌드 시점에 결정론적으로 확정 */
  ladder(answer: string, authored: readonly string[], offset: number): string[];
  /** 규칙 13 — 희귀 글자 커버(한글만 있다). 위반 메시지 목록 */
  rareCoverIssues(answer: string, authored: readonly string[]): string[];
  /** 분노 힌트 한 단위 — 한글은 초성, 라틴은 글자 자체 */
  hint(ch: string): string;
  /** authored 오답 제안 — 있으면 `src/authoring` 이 이것을 쓴다(한글은 자모 이웃으로 직접 채운다) */
  readonly suggestDecoys: ((answer: string, want: number) => string[]) | null;
}

// ── 한글 ──────────────────────────────────────────────────────────────────

const CHO_CHARS = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ';

/** 초성 한 글자 — 분노 힌트(RULES §1.16) */
export function choOf(ch: string): string {
  return CHO_CHARS.charAt(decompose(ch).cho);
}

function hangulRareCover(answer: string, authored: readonly string[]): string[] {
  const out: string[] = [];
  for (const ach of answer) {
    const raw = decompose(ach);
    const norm = normalizeJamo(raw);
    // 희귀 자모(겹받침·쌍자음·복합모음)마다 같은 클래스의 authored 오답이 2장 이상 있어야 한다.
    // 정확 일치(ㄺ↔ㄺ)와 클래스 일치(ㄺ↔ㄻ, 둘 다 ㄹ로 정규화) 둘 다 커버로 센다.
    const slots: readonly ['cho' | 'jung' | 'jong', number, number][] = [
      ['cho', raw.cho, norm.cho],
      ['jung', raw.jung, norm.jung],
      ['jong', raw.jong, norm.jong],
    ];
    for (const [slot, rawV, normV] of slots) {
      if (rawV === normV) continue; // 희귀 자모가 아니다
      let cover = 0;
      for (const dch of authored) {
        const dn = normalizeJamo(decompose(dch));
        if (dn[slot] === normV) cover++;
      }
      if (cover < 2) out.push(`'${ach}' 의 희귀 ${slot} 커버 오답 ${cover} < 2`);
    }
  }
  return out;
}

export const HANGUL: Alphabet = {
  id: 'hangul',
  ladderMin: 24,
  trapMinPerSlot: 4,
  charClass: '가-힣',
  charLabel: '완성형',
  isTile: (ch) => /^[가-힣]$/.test(ch),
  similarity: jamoSimilarity,
  ladder: hangulLadder,
  rareCoverIssues: hangulRareCover,
  hint: choOf,
  suggestDecoys: null,
};

// ── 라틴 ──────────────────────────────────────────────────────────────────

/**
 * 시각·음운 혼동 부류 — 자모 유사도의 라틴 대응(RULES §1.17). 한 글자가 여러 부류에 들고,
 * 모든 글자가 부류 짝을 4개 이상 갖는다(짝이 3개면 정답 안 글자를 빼고 나면 함정 2개가 안 나온다).
 */
export const LATIN_CLASSES: readonly string[] = [
  'BDPQ',
  'MNH',
  'CEOA',
  'ILJT',
  'UVWY',
  'FRK',
  'GQY',
  'SZX',
  'KCQ',
  'FPT',
  'CGO',
  'HKX',
  'MWV',
  'NUZ',
  'RPB',
  'SCG',
  'AEIOU',
  'DGQ',
  'JUY',
  'LEF',
];
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const isLatin = (ch: string): boolean => ch.length === 1 && ch >= 'A' && ch <= 'Z';

function latinSimilarity(a: string, b: string): number {
  if (!isLatin(a) || !isLatin(b)) return 0;
  if (a === b) return 3;
  for (const cls of LATIN_CLASSES) if (cls.includes(a) && cls.includes(b)) return 2;
  return Math.abs(LETTERS.indexOf(a) - LETTERS.indexOf(b)) <= 2 ? 1 : 0;
}

/** 같은 부류 글자(부류 순 · 중복 없음) */
function latinMates(ch: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>([ch]);
  for (const cls of LATIN_CLASSES) {
    if (!cls.includes(ch)) continue;
    for (const c of cls) {
      if (!seen.has(c)) {
        seen.add(c);
        out.push(c);
      }
    }
  }
  return out;
}

/** 알파벳 거리 1, 2, 3 … (앞쪽 먼저) — 부류 밖의 채움용 */
function latinFar(ch: string, skip: ReadonlySet<string>): string[] {
  const out: string[] = [];
  const i = LETTERS.indexOf(ch);
  for (let d = 1; d < LETTERS.length; d++) {
    for (const c of [LETTERS.charAt(i - d), LETTERS.charAt(i + d)]) {
      if (c !== '' && c !== ch && !skip.has(c)) out.push(c);
    }
  }
  return out;
}

/** 이웃 글자 — 같은 부류 → 알파벳 거리 순. 결정론적. */
function latinNeighbors(ch: string): string[] {
  const mates = latinMates(ch);
  return [...mates, ...latinFar(ch, new Set(mates))];
}

/** 한글 `buildLadder` 와 같은 조립 — 글자별 이웃 목록을 offset 부터 라운드로빈, authored 가 앞. */
function latinLadder(answer: string, authored: readonly string[], offset: number): string[] {
  const answerSet = new Set(answer.split(''));
  const perLetter = answer
    .split('')
    .map((ch) => latinNeighbors(ch).filter((c) => !answerSet.has(c)));
  const generated: string[] = [];
  const taken = new Set<string>();
  const len = perLetter.length;
  const start = len === 0 ? 0 : ((offset % len) + len) % len;
  // 라틴은 글자별 목록이 같은 26자를 다른 순서로 담아 크게 겹친다 — 한 라운드가 전부 중복이어도 목록이 남았으면 계속 돈다
  const rounds = Math.max(0, ...perLetter.map((l) => l.length));
  for (let round = 0; round < rounds; round++) {
    for (let k = 0; k < len; k++) {
      const c = perLetter[(start + k) % len]?.[round];
      if (c === undefined || taken.has(c)) continue;
      taken.add(c);
      generated.push(c);
    }
  }
  const head: string[] = [];
  const headSeen = new Set<string>();
  for (const c of authored) {
    if (headSeen.has(c) || answerSet.has(c)) continue;
    headSeen.add(c);
    head.push(c);
  }
  return [...head, ...generated.filter((c) => !headSeen.has(c))];
}

/** authored 오답 제안 — 모든 글자의 혼동 부류를 먼저 라운드로빈으로 다 쓰고, 그래도 모자라면 알파벳 이웃. */
function latinSuggest(answer: string, want: number): string[] {
  const answerSet = new Set(answer.split(''));
  const out: string[] = [];
  const seen = new Set<string>();
  const drain = (lists: readonly string[][]): void => {
    for (let round = 0; out.length < want && round < LETTERS.length; round++) {
      for (const list of lists) {
        const c = list[round];
        if (c !== undefined && !seen.has(c) && !answerSet.has(c)) {
          seen.add(c);
          out.push(c);
          if (out.length >= want) return;
        }
      }
    }
  };
  const letters = answer.split('');
  drain(letters.map((ch) => latinMates(ch)));
  if (out.length < want) drain(letters.map((ch) => latinFar(ch, new Set(latinMates(ch)))));
  return out;
}

export const LATIN: Alphabet = {
  id: 'latin',
  ladderMin: 12,
  trapMinPerSlot: 2,
  charClass: 'A-Z',
  charLabel: '영문 대문자',
  isTile: isLatin,
  similarity: latinSimilarity,
  ladder: latinLadder,
  rareCoverIssues: () => [],
  hint: (ch) => ch,
  suggestDecoys: latinSuggest,
};

// ── 로케일 프로필 ──────────────────────────────────────────────────────────

export interface LocaleProfile {
  readonly locale: Locale;
  readonly alphabet: Alphabet;
  /** 판당 문제별 정답 길이 — 합이 판당 총 글자 수(길이 경제) */
  readonly wordLenProfile: readonly number[];
  readonly minLen: number;
  readonly maxLen: number;
  readonly answerRe: RegExp;
}

export const LOCALES: Readonly<Record<Locale, LocaleProfile>> = {
  ko: {
    locale: 'ko',
    alphabet: HANGUL,
    wordLenProfile: [2, 2, 3, 3, 3, 3, 4, 4],
    minLen: 2,
    maxLen: 4,
    answerRe: /^[가-힣]{2,4}$/,
  },
  en: {
    locale: 'en',
    alphabet: LATIN,
    wordLenProfile: [4, 4, 4, 4, 5, 5, 5, 5],
    minLen: 4,
    maxLen: 5,
    answerRe: /^[A-Z]{4,5}$/,
  },
};

/** 팩의 로케일 — 없으면 ko. 알 수 없는 값은 ko 로 떨어뜨리지 않고 throw 한다(조용히 한글 규칙으로 검사하면 안 된다). */
export function localeOf(p: { readonly locale?: string | undefined }): Locale {
  const l = p.locale ?? 'ko';
  if (l !== 'ko' && l !== 'en') throw new Error(`알 수 없는 로케일 '${l}'`);
  return l;
}

export const profileOf = (p: { readonly locale?: string | undefined }): LocaleProfile =>
  LOCALES[localeOf(p)];

/** 팩 규격 — 프로필 × 3판(세션 첫 3판이 팩을 무중복 소진 · RULES §1.8) */
export function packShapeFor(profile: readonly number[]): Record<number, number> {
  const out: Record<number, number> = {};
  for (const L of profile) out[L] = (out[L] ?? 0) + 3;
  return out;
}
